import { useQuery } from '@apollo/client';
import { Typography } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { AreaChart } from '../../areaChart';
import {
  GetDashboardQuestionWidgetData,
  ListCollectionResults,
} from '../../graphql/queries/J1';
import {
  conditionallyFormatAsDate,
  j1QLResultsToLineChartData,
} from '../../j1QueryChart/lineChartUtils';
import { JupiterTheme } from '../../theme/types';
import { DashboardType } from '../../types';
import {
  DATE_EUROPEAN_MONTH_FORMAT,
  endOfDay,
  startOfDay,
  subMinutes,
  subMonths,
  subWeeks,
  subYears,
} from '../../util/dateFns';

enum EDateSpan {
  week = '1W',
  month = '1M',
  quarter = '3M',
  year = '1Y',
}

const END_DATE = new Date();
const WEEK_AGO_DATE = startOfDay(subWeeks(END_DATE, 1));
const MONTH_AGO_DATE = startOfDay(subMonths(END_DATE, 1));
const QUARTER_AGO_DATE = startOfDay(subMonths(END_DATE, 4));
const YEAR_AGO_DATE = startOfDay(subYears(END_DATE, 1));

const TIMESPAN = {
  end: endOfDay(END_DATE).getTime(), // Date's Midnight for local timezone.
  [EDateSpan.week]: subMinutes(
    WEEK_AGO_DATE,
    WEEK_AGO_DATE.getTimezoneOffset()
  ).getTime(),
  [EDateSpan.month]: subMinutes(
    MONTH_AGO_DATE,
    MONTH_AGO_DATE.getTimezoneOffset()
  ).getTime(),

  [EDateSpan.quarter]: subMinutes(
    QUARTER_AGO_DATE,
    QUARTER_AGO_DATE.getTimezoneOffset()
  ).getTime(),

  [EDateSpan.year]: subMinutes(
    YEAR_AGO_DATE,
    YEAR_AGO_DATE.getTimezoneOffset()
  ).getTime(),
};

type Metric = { name: string; value: number };
type MetricItem = { timestamp: number; outputs: Metric[] };
type MetricByName = { [name: string]: { [ts: number]: number } | undefined };
type Timestamps = { [ts: number]: boolean };
type MetricsAcc = [Timestamps, MetricByName];

const DATE_RANGES = [
  EDateSpan.week,
  EDateSpan.month,
  EDateSpan.quarter,
  EDateSpan.year,
];

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    root: {
      marginBottom: theme.spacing(2),
    },
    dateRange: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: theme.spacing(),
    },
    button: {
      height: theme.spacing(4),
      color: theme.palette.navy[600],
      '&:hover': {
        backgroundColor: theme.palette.navy[50],
      },
    },
    selected: {
      '&&': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.navy[600],
        '&:hover': {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.navy[600],
        },
      },
    },
  })
);

type DataItem = {
  x: string;
  y: number | null;
};

interface Props {
  ownerId?: string;
  dashboardId?: string;
  dashboardType?: DashboardType;
  widgetId?: string;
  questionId: string;
  onTrendDataLoaded?: (hasValidData: boolean) => void;
  height?: string | number;
  width?: string | number;
  className?: string;
}

const QuestionTrendChart = ({
  ownerId,
  dashboardId,
  dashboardType,
  widgetId,
  questionId,
  onTrendDataLoaded,
  height,
  width,
  className,
}: Props) => {
  const classes = useStyles();

  const [dateRange, setDateRange] = useState<EDateSpan>(EDateSpan.month);

  // TODO: This could be better for performing queries to get question data
  const isDashboardQuery = dashboardId && dashboardType && widgetId;
  let gqlQuery;
  if (isDashboardQuery) {
    gqlQuery = {
      query: GetDashboardQuestionWidgetData,
      variables: {
        ownerId,
        dashboardId,
        widgetId,
        dashboardType,
        beginTimestamp: TIMESPAN[dateRange],
        endTimestamp: TIMESPAN.end,
      },
    };
  } else {
    gqlQuery = {
      query: ListCollectionResults,
      variables: {
        collectionType: 'QUESTION',
        collectionOwnerId: questionId,
        beginTimestamp: TIMESPAN[dateRange],
        endTimestamp: TIMESPAN.end,
      },
    };
  }

  const { data: metricsData, loading } = useQuery(gqlQuery.query, {
    variables: gqlQuery.variables,
  });

  const metrics = isDashboardQuery
    ? metricsData?.getDashboardQuestionWidgetData?.data.results || []
    : metricsData?.listCollectionResults?.results || [];

  // Controls visibility of "Add to dashboard" icon button in the parent scope.
  useEffect(() => {
    if (!loading) {
      const hasValidData = metrics.length > 1; // Need at least 2 datapoints.
      onTrendDataLoaded?.(hasValidData);
    }
  }, [loading]);

  const handleDateSpanChange = (
    _: React.MouseEvent<HTMLElement>,
    dateSpan: EDateSpan
  ) => {
    setDateRange(dateSpan);
  };

  const [timestamps, byName] = metrics.reduce(
    (acc: MetricsAcc, metric: MetricItem) => {
      const [timestamps, byName] = acc;
      timestamps[metric.timestamp] = true;
      metric.outputs.forEach((output) => {
        if (typeof byName[output.name] !== 'object') {
          byName[output.name] = {};
        }
        byName[output.name]![metric.timestamp] = output.value;
      });
      return [timestamps, byName] as MetricsAcc;
    },
    [{}, {}]
  );

  const sortedTimestamps = Object.keys(timestamps).sort();

  const datas = Object.keys(byName).map((fullName) => ({
    data: sortedTimestamps.map((ts: any) => ({
      x: ts,
      y: byName[fullName][ts] || null,
      line: fullName.replace(/^queries\./, '').replace(/\.total$/, ''),
    })),
  }));

  const chartData: DataItem[][] = Object.values(datas).map((d) => d.data);
  const [data, yLabels] = j1QLResultsToLineChartData(chartData);

  return (
    <div className={clsx(classes.root, className)}>
      <ToggleButtonGroup
        value={dateRange}
        exclusive
        aria-label="date range selector"
        onChange={handleDateSpanChange}
        className={classes.dateRange}
      >
        {DATE_RANGES.map((dateRange) => (
          <ToggleButton
            key={dateRange}
            value={dateRange}
            classes={{
              root: classes.button,
              selected: classes.selected,
            }}
          >
            <Typography variant="caption">{dateRange}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {loading ? (
        <LinearProgress />
      ) : (
        <AreaChart
          data={data}
          height={height}
          width={width}
          x={{
            key: 'x',
            formatter: conditionallyFormatAsDate,
          }}
          y={{
            keys: yLabels.map((yKey) => ({ key: yKey })),
          }}
          tooltip={{
            formatter: (value: string | number) => {
              const formattedValue =
                typeof value === 'string' ? parseInt(value, 10) : value;
              return format(
                new Date(formattedValue),
                DATE_EUROPEAN_MONTH_FORMAT
              );
            },
          }}
          legend={{
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'top',
            wrapperStyle: {
              paddingLeft: 32,
              paddingBottom: 32,
              height: '100%',
            },
          }}
          animate
        />
      )}
    </div>
  );
};
export default QuestionTrendChart;
